import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useBreadcrumb, type BreadcrumbItem } from "@/contexts/BreadcrumbContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

type ShipmentEmailPreview = {
  to: string;
  subject: string;
  html: string;
};

export function OrderShipmentEmailPreview() {
  const { orderId } = useParams<{ orderId: string }>();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [preview, setPreview] = useState<ShipmentEmailPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    const crumbs: BreadcrumbItem[] = [
      { label: "Sales", path: "/products" },
      { label: "Orders", path: "/sales/orders" },
      { label: "Edit order", path: `/sales/orders/${orderId}` },
      { label: "Shipment email preview", path: `/orders/${orderId}/shipment/email/preview` },
    ];
    setBreadcrumbs(crumbs);
    return () => {
      clearBreadcrumbs();
    };
  }, [orderId, setBreadcrumbs, clearBreadcrumbs]);

  useEffect(() => {
    async function fetchPreview() {
      if (!orderId) return;
      setIsLoading(true);
      setError(null);
      setPreview(null);
      try {
        const response = await fetch(`/api/orders/${orderId}/shipment/email/preview`);
        const data = (await response.json().catch(() => ({}))) as {
          preview?: ShipmentEmailPreview;
          error?: string;
        };
        if (!response.ok) {
          setError(typeof data.error === "string" ? data.error : "Could not load preview");
          return;
        }
        if (!data.preview?.html || !data.preview.subject) {
          setError("Preview response was incomplete");
          return;
        }
        setPreview(data.preview);
      } catch (e) {
        console.error(e);
        setError("Could not load preview");
      } finally {
        setIsLoading(false);
      }
    }

    void fetchPreview();
  }, [orderId]);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 sm:px-6">
        <div className="flex justify-center items-center min-h-[40vh]">
          <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 py-4 space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to={orderId ? `/sales/orders/${orderId}` : "/sales/orders"}>← Back to order</Link>
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Shipment email preview</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Preview uses live Easyship tracking data for this order.
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      ) : preview ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>To</Label>
            <p className="text-sm">{preview.to}</p>
          </div>
          <div className="space-y-2">
            <Label>Subject</Label>
            <p className="text-sm font-medium">{preview.subject}</p>
          </div>
          <div className="space-y-2">
            <Label>Body</Label>
            <div className="rounded-lg border overflow-hidden bg-white">
              <iframe
                title="Shipment email preview"
                srcDoc={preview.html}
                className="w-full min-h-[640px] border-0"
                sandbox=""
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
